import { useEffect, useRef, useState } from "react";
import CryptoJS from "crypto-js";
import { Card } from "@mui/material";
import SoftBox from "components/SoftBox";
import PageLayout from "examples/LayoutContainers/PageLayout";
import env from "config";
import { useAgencyInfo } from "context/agency";
import { useKnowledgeBase } from "context/knowledgebaseContext";
import { useAppServices } from "hook/services";
import PageTitle from "../components/PageTitle";
import { appendDevProfileParams, resolveLocationLaunchOverride } from "./leadSourceLaunch";
import PurchaseEmailDialog from "./PurchaseEmailDialog";
import "./style.css";

// Trailing space is part of the TLS prefix. Do not drop it.
const EMAIL_HASH_SALT = "tspearshash ";

// Known-working LeadSource test launch values (Viber link).
const STATIC = {
  fullname: "Martin Sanders",
  email: "martin.sanders@ggmail.com",
  plan: "aca4e89324",
  locationid: "xExHL1rJ9XuYNXJiuNdq",
};

/**
 * Isolate auth variables one-by-one.
 * Start with ALL false (= 100% static working link).
 * Flip ONE flag to true at a time, deploy/test, then next.
 */
const USE_DYNAMIC = {
  fullname: true, // STEP 3 OK
  email: true, // business/location email + emailhash — STEP 1 OK
  plan: true, // email DB lookup, or TEMP location override
  locationid: true, // always agency.ghl.location_id (never parent URL Mongo id)
};

function normalizeEmail(value) {
  if (!value) return "";
  let email = String(value).trim();
  try {
    email = decodeURIComponent(email);
  } catch {
    /* already decoded */
  }
  // Same string TLS hashes: trim, lowercase, NFKC. Hash is computed fresh from this.
  return email.trim().toLowerCase().normalize("NFKC");
}

/** fullname stays the sub-account / business name. Never the person's first + last. */
function resolveBusinessName(agency) {
  const locationName = (agency?.location_name || "").trim();
  if (locationName) return locationName;
  const agencyName = (agency?.name || "").trim();
  if (agencyName && !agencyName.includes("@")) return agencyName;
  return "";
}

/**
 * A saved profile string wins, including an explicit blank (so GHL is not
 * sent after the user clears the field). GHL is only the default before
 * the profile key exists.
 */
function resolveProfileName(profileValue, locationValue) {
  if (typeof profileValue === "string") return profileValue.trim();
  return (locationValue || "").trim();
}

function buildLeadSourceLaunchUrl({
  baseUrl,
  fullname,
  email,
  plan,
  locationId,
  firstName,
  lastName,
  alternateEmail,
  phone,
}) {
  const normalizedEmail = normalizeEmail(email);
  const emailhash = CryptoJS.MD5(`${EMAIL_HASH_SALT}${normalizedEmail}`).toString();
  const params = new URLSearchParams();
  params.set("fullname", fullname || "");
  params.set("email", normalizedEmail);
  params.set("emailhash", emailhash);
  params.set("plan", plan || "");
  params.set("locationid", locationId || "");
  const base = (baseUrl || env.LEADSOURCE_LAUNCH_URL || "").replace(/\?$/, "");
  appendDevProfileParams(params, base, { firstName, lastName, alternateEmail, phone });
  return `${base}?${params.toString()}`;
}

function Leads({ onLaunched }) {
  const [agency] = useAgencyInfo();
  const { knowledgeBase, loading } = useKnowledgeBase();
  const AppService = useAppServices();
  const launchUrlRef = useRef("");
  const [iframeSrc, setIframeSrc] = useState("");
  const [resolving, setResolving] = useState(true);
  const [missing, setMissing] = useState([]);
  const [promptMode, setPromptMode] = useState(null);
  const [purchaseEmail, setPurchaseEmail] = useState("");
  const [checkingPurchase, setCheckingPurchase] = useState(false);

  // Auto-launch only if location email is already in the purchase DB.
  // Otherwise ask for purchase email and pass *that* email to LeadSource (do not
  // write location emails into the purchase table — that created duplicate accounts).
  const businessEmail = normalizeEmail(agency?.location_email || agency?.email);
  const businessName = resolveBusinessName(agency);
  const profileFirstName = resolveProfileName(
    agency?.profile?.firstName,
    agency?.location_first_name,
  );
  const profileLastName = resolveProfileName(
    agency?.profile?.lastName,
    agency?.location_last_name,
  );
  const profileAlternateEmail = (agency?.profile?.alternateEmail || "").trim();
  const profilePhone = (agency?.profile?.phone_number || "").trim();
  // Always GHL location ID for LeadSource — never Mongo agency _id from the parent URL.
  const ghlLocationId = (agency?.ghl?.location_id || "").trim();
  const fallbackPlan = (env.LEADSOURCE_PLAN_ID || STATIC.plan).trim();

  const lookupPlanCode = async (email) => {
    const { response } = await AppService.leadsource.lookupPlan({
      query: `email=${encodeURIComponent(email)}`,
      token: true,
    });
    return response?.data?.found && response?.data?.planCode
      ? String(response.data.planCode).trim()
      : "";
  };

  // Purchase email this location already verified once, with its current plan.
  const lookupSavedPurchase = async (locationId) => {
    const { response } = await AppService.leadsource.lookupLocationEmail({
      query: `locationId=${encodeURIComponent(locationId)}`,
      token: true,
    });
    const data = response?.data;
    if (!data?.found || !data?.planCode || !data?.purchaseEmail) return null;
    return {
      email: normalizeEmail(data.purchaseEmail),
      plan: String(data.planCode).trim(),
    };
  };

  // Best effort: a failed save only means the popup shows again next time.
  const rememberPurchaseEmail = async (locationId, email) => {
    try {
      await AppService.leadsource.saveLocationEmail({
        payload: { locationId, purchaseEmail: email },
        token: true,
      });
    } catch {
      /* ignore */
    }
  };

  const launchIframe = ({ email, fullname, locationId, plan, launchBase }) => {
    const built = buildLeadSourceLaunchUrl({
      baseUrl: launchBase,
      fullname,
      email,
      plan,
      locationId,
      firstName: profileFirstName,
      lastName: profileLastName,
      alternateEmail: profileAlternateEmail,
      phone: profilePhone,
    });
    launchUrlRef.current = built;
    setMissing([]);
    setPromptMode(null);
    setIframeSrc(built);
    setResolving(false);
  };

  useEffect(() => {
    if (iframeSrc) onLaunched?.();
  }, [iframeSrc, onLaunched]);

  useEffect(() => {
    let cancelled = false;
    launchUrlRef.current = "";

    async function resolveLaunchUrl() {
      const email = USE_DYNAMIC.email ? businessEmail : STATIC.email;
      const fullname = USE_DYNAMIC.fullname ? businessName : STATIC.fullname;
      const locationId = USE_DYNAMIC.locationid ? ghlLocationId : STATIC.locationid;

      const gaps = [];
      if (!locationId) gaps.push("GHL location ID");

      if (gaps.length) {
        if (!cancelled) {
          setMissing(gaps);
          setPromptMode(null);
          setIframeSrc("");
          setResolving(false);
        }
        return;
      }

      const locationOverride = resolveLocationLaunchOverride(locationId);
      let plan = USE_DYNAMIC.plan ? null : STATIC.plan;
      let launchBase = env.LEADSOURCE_LAUNCH_URL;
      let launchEmail = email;
      const launchFullname = fullname;

      if (locationOverride?.plan) {
        plan = locationOverride.plan;
      } else if (USE_DYNAMIC.plan) {
        if (email) {
          const lookedUp = await lookupPlanCode(email);
          if (lookedUp) plan = lookedUp;
        }
        if (!plan) {
          // Location email has no plan: reuse the purchase email this location
          // already verified instead of asking for it on every visit.
          const saved = await lookupSavedPurchase(locationId);
          if (saved) {
            plan = saved.plan;
            launchEmail = saved.email;
          }
        }
      } else {
        plan = fallbackPlan || STATIC.plan;
      }

      if (locationOverride?.launchUrl) {
        launchBase = locationOverride.launchUrl;
      }

      if (!launchBase) gaps.push("LeadSource launch URL");
      if (gaps.length) {
        if (!cancelled) {
          setMissing(gaps);
          setPromptMode(null);
          setIframeSrc("");
          setResolving(false);
        }
        return;
      }

      if (plan && launchEmail) {
        if (!cancelled) {
          launchIframe({
            email: launchEmail,
            fullname: launchFullname,
            locationId,
            plan,
            launchBase,
          });
        }
        return;
      }

      // No location email, or location email not in the plan table → purchase email popup.
      if (!cancelled) {
        setMissing([]);
        setIframeSrc("");
        setPromptMode("purchase");
        setResolving(false);
      }
    }

    setResolving(true);
    setPromptMode(null);
    resolveLaunchUrl();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    businessEmail,
    businessName,
    ghlLocationId,
    fallbackPlan,
    profileFirstName,
    profileLastName,
    profileAlternateEmail,
    profilePhone,
  ]);

  const handlePurchaseSubmit = async (event) => {
    event.preventDefault();
    const billingEmail = normalizeEmail(purchaseEmail);
    if (!billingEmail) return;

    const locationId = USE_DYNAMIC.locationid ? ghlLocationId : STATIC.locationid;
    // Always launch LeadSource as the purchase email — never copy location emails
    // into the purchase DB (bindFromPurchase), or each sub-account becomes its own Leads account.
    const fullname = USE_DYNAMIC.fullname ? businessName : STATIC.fullname;
    const launchBase =
      resolveLocationLaunchOverride(locationId)?.launchUrl || env.LEADSOURCE_LAUNCH_URL;

    if (!locationId || !launchBase) {
      setPromptMode("notFound");
      return;
    }

    setCheckingPurchase(true);
    try {
      const plan = await lookupPlanCode(billingEmail);
      if (plan) {
        await rememberPurchaseEmail(locationId, billingEmail);
        launchIframe({
          email: billingEmail,
          fullname,
          locationId,
          plan,
          launchBase,
        });
        return;
      }

      setPromptMode("notFound");
    } finally {
      setCheckingPurchase(false);
    }
  };

  // const handlePurchaseSubmit = async (event) => {
  //   event.preventDefault();
  //   const email = normalizeEmail(purchaseEmail);
  //   if (!email) return;

  //   const locationId = USE_DYNAMIC.locationid ? dynamicLocationId : STATIC.locationid;
  //   const fullname = USE_DYNAMIC.fullname
  //     ? resolveFullname(agency?.location_name || agency?.name, email)
  //     : STATIC.fullname;
  //   const launchBase =
  //     resolveLocationLaunchOverride(locationId)?.launchUrl || env.LEADSOURCE_LAUNCH_URL;

  //   setCheckingPurchase(true);
  //   try {
  //     const plan = await lookupPlanCode(email);
  //     if (plan && launchBase) {
  //       launchIframe({ email, fullname, locationId, plan, launchBase });
  //       return;
  //     }
  //     setPromptMode("notFound");
  //   } finally {
  //     setCheckingPurchase(false);
  //   }
  // };

  return (
    <PageLayout>
      <SoftBox className="sd-leads sd-page" width="100%">
        <div className="sd-leads__header sd-page__header d-flex w-100 align-items-center">
          <PageTitle helpUrl={!loading && knowledgeBase?.leads}>Leads</PageTitle>
        </div>

        <Card
          className="sd-page-content-card sd-leads__card"
          sx={{
            padding: 0,
            borderRadius: "12px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            backgroundColor: "#fff",
            overflow: "hidden",
          }}
        >
          {iframeSrc ? (
            <iframe
              className="sd-leads__iframe"
              src={iframeSrc}
              title="Leads"
              allow="clipboard-write"
            />
          ) : (
            <SoftBox className="sd-leads__empty">
              {resolving
                ? "Loading Leads…"
                : missing.length
                  ? `Unable to open Leads. Missing: ${missing.join(", ")}.`
                  : "Enter your Smart Directory AI purchase email to open Leads."}
            </SoftBox>
          )}
        </Card>
      </SoftBox>

      <PurchaseEmailDialog
        open={Boolean(promptMode) && !iframeSrc}
        mode={promptMode}
        value={purchaseEmail}
        submitting={checkingPurchase}
        onChange={setPurchaseEmail}
        onSubmit={handlePurchaseSubmit}
        onTryAgain={() => setPromptMode("purchase")}
      />
    </PageLayout>
  );
}

export default Leads;
